<template>
  <div v-if="clientStore.isLoading">
    <span class="loading loading-spinner loading-lg text-base-content/60"></span>
  </div>
  <div v-else class="mt-6 bg-base-100 rounded-lg p-6">
    <div>
      <div class="flex justify-between items-center mb-6">
        <h2 class="text-xl font-semibold">
          {{ isEditing ? "Redaguoti keleivio informaciją" : "Naujas keleivis" }}
          <div
            v-if="formData.service_restricted"
            class="badge badge-error font-normal"
          >
            Apribotos paslaugos
          </div>
        </h2>

        <button
          v-if="isEditing"
          @click="confirmDelete(formData.id)"
          class="btn btn-sm btn-ghost"
          title="Ištrinti"
        >
          <DefaultIcon name="trash" size="w-5 h-5" />
        </button>
      </div>

      <form @submit.prevent="handleSubmit" class="space-y-6">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <fieldset class="fieldset w-full">
            <legend class="fieldset-legend">Vardas*</legend>
            <input
              v-model="formData.name"
              type="text"
              class="input w-full"
              required
            />
          </fieldset>

          <fieldset class="fieldset w-full">
            <legend class="fieldset-legend">Pavardė*</legend>
            <input
              v-model="formData.surname"
              type="text"
              class="input w-full"
              required
            />
          </fieldset>

          <fieldset class="fieldset w-full">
            <legend class="fieldset-legend">Telefonas*</legend>
            <div class="relative">
              <span
                class="absolute left-3 top-1/2 -translate-y-1/2 text-base-content/60 z-10"
                >+370</span
              >
              <input
                :value="formData.phone?.replace('+370', '')"
                @input="(e) => (formData.phone = '+370' + e.target.value)"
                placeholder="61111111"
                type="text"
                maxlength="8"
                class="input w-full pl-14"
              />
            </div>
          </fieldset>

          <fieldset class="fieldset w-full">
            <legend class="fieldset-legend">El. paštas</legend>
            <input
              v-model="formData.email"
              type="email"
              class="input w-full"
            />
          </fieldset>

          <AddressSearchInput
            v-model="formData.address"
            :required="false"
            class="w-full"
          />

          <fieldset class="fieldset w-full">
            <legend class="fieldset-legend">Paslauga galioja iki</legend>
            <input
              v-model="formData.valid_until"
              type="date"
              class="input w-full md:w-56"
            />
          </fieldset>

          <fieldset class="fieldset w-full">
            <legend class="fieldset-legend">Paslaugų teikimo pagrindas</legend>
            <select
              v-model="formData.service_type"
              class="select w-full"
            >
              <option :value="null">— nepasirinkta —</option>
              <option
                v-for="(
                  config, key
                ) in referenceDataStore.clientServiceTypeConfig"
                :key="key"
                :value="key"
              >
                {{ config.text }}
              </option>
            </select>
          </fieldset>

          <fieldset class="fieldset w-full">
            <legend class="fieldset-legend">Apribotas paslaugų teikimas iki:</legend>
            <input
              v-model="formData.service_restriction_until"
              type="date"
              class="input w-full md:w-56"
            />
          </fieldset>
        </div>
        <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div class="flex md:mt-6 gap-4">
            <input
              v-model="formData.disabled"
              type="checkbox"
              class="checkbox"
            />
            <span>Neįgaliojo vežimėlyje?</span>
          </div>

          <div class="flex md:mt-6 gap-4">
            <input
              v-model="formData.child_seat_needed"
              type="checkbox"
              class="checkbox"
            />
            <span>Reikalinga vaikiška kėdutė?</span>
          </div>

          <div class="flex md:mt-6 gap-4">
            <input
              v-model="formData.wheelchair_needed"
              type="checkbox"
              class="checkbox"
            />
            <span>Reikalingas neįgaliojo vežimėlis?</span>
          </div>
        </div>
        <div class="grid grid-cols-1 gap-4 mb-6">
          <fieldset class="fieldset w-full">
            <legend class="fieldset-legend">Pastaba</legend>
            <textarea
              v-model="formData.description"
              class="textarea h-24 w-full"
            ></textarea>
          </fieldset>
        </div>

        <div class="flex justify-end gap-4 mt-8">
          <button type="button" class="btn btn-ghost" @click="goBack('/clients')">
            Atšaukti
          </button>
          <button
            type="submit"
            class="btn btn-success"
            :disabled="clientStore.isLoading"
          >
            {{ isEditing ? "Atnaujinti" : "Pridėti" }}
          </button>
        </div>
      </form>
    </div>
  </div>
  <div
    v-if="isEditing && !clientStore.isLoading"
    class="mt-6 bg-base-100 rounded-lg p-6"
  >
    <div>
      <div class="flex justify-between items-center mb-6">
        <h2 class="text-xl font-semibold">Kliento statistika</h2>
      </div>
      <div class="flex flex-col gap-2">
        <div>Bendras pavėžėjimų skaičius: {{ formData.trips_count }}</div>
        <div>
          Pavėžėjimų, į kuriuos klientas neatvyko, skaičius:
          {{ formData.trips_count_not_arrived }}
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { useClientStore } from "@/stores/clients";
import { useReferenceDataStore } from "@/stores/referenceData";
import { ref, onMounted, computed } from "vue";
import { useRoute } from "vue-router";
import { useGoBack } from "@/composables/navigation/useGoBack";
import AddressSearchInput from "@/components/search/AddressSearchInput.vue";
import DefaultIcon from "@/components/icons/DefaultIcon.vue";

const route = useRoute();
const goBack = useGoBack();
const clientStore = useClientStore();
const referenceDataStore = useReferenceDataStore();

const isEditing = computed(() => !!route.params.id);

const formData = ref({
  name: null,
  surname: null,
  email: null,
  phone: null,
  address: null,
  valid_until: null,
  service_type: null,
  service_restriction_until: null,
  description: null,
  child_seat_needed: false,
  wheelchair_needed: false,
  trips_count: 0,
  trips_count_not_arrived: 0,
});

onMounted(async () => {
  await referenceDataStore.ensureDataLoaded();

  if (isEditing.value) {
    try {
      const client = await clientStore.fetchClientById(route.params.id);
      if (client) {
        formData.value = { ...client };
      } else {
        goBack("/clients");
      }
    } catch (error) {
      console.error("Failed to fetch client:", error);
      goBack("/clients");
    }
  }
});

const handleSubmit = async () => {
  try {
    if (isEditing.value) {
      await clientStore.updateClient(route.params.id, formData.value);
    } else {
      await clientStore.addClient(formData.value);
    }
    goBack("/clients");
  } catch (error) {
    console.error("Failed to save client:", error);
  }
};

const confirmDelete = async (id) => {
  if (confirm("Ar tikrai norite ištrinti keleivį ir visą jo informaciją?")) {
    try {
      await clientStore.deleteClient(id);
      goBack("/clients");
    } catch (error) {
      console.error("Failed to delete client:", error);
    }
  }
};
</script>
